import { useCallback, useEffect, useState } from 'react'
import { adDetectionApi } from '../../api'
import { ErrorState, errorMessage } from '../../components/ErrorState'
import type { AdSearchQuery } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { Block, PageHeader } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import { useAds } from './AdsContext'
import { categoryLabel, GOOGLE_PAGES_PER_QUERY, QUERY_CATEGORIES } from './adsShared'
import { countryName, inCountry } from './AdsModule'

export function AdsQueries() {
  const { t, fmt } = useI18n()
  const { country } = useShell()
  const [queries, setQueries] = useState<AdSearchQuery[] | null>(null)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    adDetectionApi.getQueries().then(setQueries).catch((err) => setError(errorMessage(err)))
  }, [])
  useEffect(load, [load])

  const run = async (action: () => Promise<unknown>) => {
    setError(null)
    try {
      await action()
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const playedHere = (q: AdSearchQuery) => !country || !q.countries?.length || q.countries.includes(country)
  const term = search.trim().toLowerCase()
  const shown = (queries ?? []).filter(playedHere).filter((q) => !term || q.query.toLowerCase().includes(term))
  const groups = shown.reduce<Record<string, AdSearchQuery[]>>((acc, q) => {
    ;(acc[q.category] ??= []).push(q)
    return acc
  }, {})
  const activeHere = (queries ?? []).filter((q) => q.isActive && playedHere(q)).length

  return (
    <>
      <PageHeader
        title={t('ads.queriesTitle')}
        description={t('ads.queriesDesc', { pages: GOOGLE_PAGES_PER_QUERY })}
      />

      <div className="ads-queries-layout">
        <div className="ads-queries-main">
          <div className="ads-filters">
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('ads.searchQueryPlaceholder')} aria-label={t('ads.searchQueryPlaceholder')} />
            <span className="block-sub">
              {country ? t('ads.playedIn', { where: inCountry(t, country) }) : t('ads.allCountriesPlayed')} ·{' '}
              {t('ads.queryCount', { n: shown.length })}
            </span>
          </div>

          {error && <ErrorState message={error} compact />}
          {queries === null ? (
            <div className="stat-skeleton" style={{ minHeight: 240 }} />
          ) : shown.length === 0 ? (
            <div className="block empty-note">{term ? t('ads.noQueryMatch') : t('ads.noQueryConfigured')}</div>
          ) : (
            Object.entries(groups)
              // Tri alphabétique dans la langue courante : « Jeux » et « Gambling »
              // ne se rangent pas au même endroit.
              .sort((a, b) => categoryLabel(t, a[0]).localeCompare(categoryLabel(t, b[0]), fmt.locale))
              .map(([category, items]) => (
                <Block
                  key={category}
                  flush
                  title={categoryLabel(t, category)}
                  subtitle={t('ads.activeOutOf', { n: items.filter((q) => q.isActive).length, total: items.length })}
                >
                  <ul className="ads-query-list">
                    {items.map((q) => (
                      <QueryRow
                        key={q.id}
                        query={q}
                        onToggle={() => run(() => adDetectionApi.updateQuery(q.id, { isActive: !q.isActive }))}
                        onSave={(patch) => run(() => adDetectionApi.updateQuery(q.id, patch))}
                        onDelete={() => {
                          if (window.confirm(t('ads.deleteQueryConfirm', { query: q.query }))) void run(() => adDetectionApi.deleteQuery(q.id))
                        }}
                      />
                    ))}
                  </ul>
                </Block>
              ))
          )}
        </div>

        <aside className="ads-queries-side">
          <AddQueryBlock defaultCountry={country} onCreated={load} />
          <Block title={country ? t('ads.scanCostTitleCountry', { where: inCountry(t, country) }) : t('ads.scanCostTitle')}>
            <div className="ads-quota-figure">
              <span className="kpi-value">~ {fmt.number(activeHere * GOOGLE_PAGES_PER_QUERY)}</span>
              <span className="block-sub">{t('ads.atMostSearches')}</span>
            </div>
            <p className="field-note">{t('ads.scanCostNote', { n: activeHere, pages: GOOGLE_PAGES_PER_QUERY })}</p>
          </Block>
        </aside>
      </div>
    </>
  )
}

function QueryRow({
  query: q,
  onToggle,
  onSave,
  onDelete,
}: {
  query: AdSearchQuery
  onToggle: () => void
  onSave: (patch: { query: string; category: string; countries: string[] }) => void
  onDelete: () => void
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(q.query)
  const [category, setCategory] = useState(q.category)
  const [countries, setCountries] = useState<string[]>(q.countries ?? [])

  const startEdit = () => {
    setText(q.query)
    setCategory(q.category)
    setCountries(q.countries ?? [])
    setEditing(true)
  }

  return (
    <li className={`ads-query${q.isActive ? '' : ' ads-query-off'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={q.isActive}
        aria-label={q.isActive ? t('ads.disableQuery', { query: q.query }) : t('ads.enableQuery', { query: q.query })}
        className="ads-switch"
        onClick={onToggle}
      >
        <span />
      </button>
      {editing ? (
        <div className="ads-query-edit form-stack">
          <div className="bet-form-row">
            <label>
              {t('ads.searchLabel')}
              <input value={text} onChange={(e) => setText(e.target.value)} autoFocus />
            </label>
            <label>
              {t('ads.sector')}
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {QUERY_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(t, c)}</option>)}
              </select>
            </label>
          </div>
          <CountryPicker value={countries} onChange={setCountries} />
          <div className="bet-form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!text.trim()}
              onClick={() => {
                onSave({ query: text.trim(), category, countries })
                setEditing(false)
              }}
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <span className="ads-query-text">« {q.query} »</span>
          <span className="ads-query-countries" title={q.countries?.length ? t('ads.queryOnlyIn') : t('ads.queryEverywhere')}>
            {q.countries?.length ? q.countries.map((c) => <span key={c} className="ads-chip">{c}</span>) : <span className="bet-muted">{t('ads.allCountriesShort')}</span>}
          </span>
          <span className="ads-query-actions">
            <button type="button" className="block-link" onClick={startEdit}>{t('common.edit')}</button>
            <button type="button" className="block-link ads-danger-link" onClick={onDelete}>{t('common.delete')}</button>
          </span>
        </>
      )}
    </li>
  )
}

function AddQueryBlock({ defaultCountry, onCreated }: { defaultCountry: string; onCreated: () => void }) {
  const t = useT()
  const [text, setText] = useState('')
  const [category, setCategory] = useState('COMMERCE')
  const [countries, setCountries] = useState<string[]>(defaultCountry ? [defaultCountry] : [])
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => setCountries(defaultCountry ? [defaultCountry] : []), [defaultCountry])

  const submit = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await adDetectionApi.createQuery(text.trim(), category, countries)
      setMessage({ ok: true, text: t('ads.addQueryDone', { query: text.trim() }) })
      setText('')
      onCreated()
    } catch (err) {
      setMessage({ ok: false, text: errorMessage(err) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Block title={t('ads.addQueryTitle')}>
      <form
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault()
          if (text.trim()) void submit()
        }}
      >
        <label>
          {t('ads.searchLabel')}
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('ads.queryPlaceholder')} />
        </label>
        <label>
          {t('ads.sector')}
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            {QUERY_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(t, c)}</option>)}
          </select>
        </label>
        <div className="ads-picker-field">
          <span>{t('ads.pickerHint')}</span>
          <CountryPicker value={countries} onChange={setCountries} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={!text.trim() || saving}>
          {saving ? t('ads.adding') : t('ads.addQuerySubmit')}
        </button>
        {message && <p className={message.ok ? 'ads-message-ok' : 'ads-message-error'} role="status">{message.text}</p>}
      </form>
    </Block>
  )
}

function CountryPicker({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const t = useT()
  const { countries } = useAds()
  const toggle = (code: string) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code])
  return (
    <div className="country-chips" role="group" aria-label={t('ads.pickerAria')}>
      <button type="button" className="country-chip" aria-pressed={value.length === 0} onClick={() => onChange([])}>
        {t('common.allCountries')}
      </button>
      {countries.map((c) => (
        <button key={c.code} type="button" className="country-chip" aria-pressed={value.includes(c.code)} title={countryName(t, c.code)} onClick={() => toggle(c.code)}>
          {c.code}
        </button>
      ))}
    </div>
  )
}
